/**
 * HR Service
 * Centralized API calls for HR dashboard
 */

import { API_TIMEOUTS, DATA_LIMITS, HR_RELEVANT_STATUSES } from '../constants/hrConstants';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
if (!BACKEND_URL) {
  throw new Error('REACT_APP_BACKEND_URL environment variable is required');
}

/**
 * Create abort controller with timeout
 */
const createAbortController = (timeout) => {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);
  return { controller, timeoutId };
};

/**
 * Fetch submissions with HR-relevant filtering
 */
export const fetchSubmissions = async () => {
  const { controller, timeoutId } = createAbortController(API_TIMEOUTS.SUBMISSIONS);
  
  try {
    const response = await fetch(`${BACKEND_URL}/api/submissions`, {
      signal: controller.signal
    });
    clearTimeout(timeoutId);
    
    if (response.ok) {
      const data = await response.json();
      const allSubmissions = data.submissions || [];
      // Filter to only onboarding-relevant statuses for HR dashboard
      const hrRelevant = allSubmissions.filter(s => 
        HR_RELEVANT_STATUSES.includes(s.status)
      );
      return { success: true, data: hrRelevant };
    } else {
      console.error('Failed to fetch submissions:', response.status);
      return { success: false, data: [], error: `HTTP ${response.status}` };
    }
  } catch (error) {
    clearTimeout(timeoutId);
    if (error.name === 'AbortError') {
      console.error('Submissions fetch timeout');
      return { success: false, data: [], error: 'Timeout' };
    }
    console.error('Error fetching submissions:', error);
    return { success: false, data: [], error: error.message };
  }
};

/**
 * Fetch candidates with limit
 */
export const fetchCandidates = async () => {
  const { controller, timeoutId } = createAbortController(API_TIMEOUTS.CANDIDATES);
  
  try {
    const response = await fetch(
      `${BACKEND_URL}/api/candidates?limit=${DATA_LIMITS.CANDIDATES}`,
      { signal: controller.signal }
    );
    clearTimeout(timeoutId);
    
    if (response.ok) {
      const data = await response.json();
      const candidates = Array.isArray(data) ? data : (data.candidates || []);
      console.log(`Loaded ${candidates.length} candidates`);
      return { success: true, data: candidates };
    } else {
      console.error('Failed to fetch candidates:', response.status);
      return { success: false, data: [], error: `HTTP ${response.status}` };
    }
  } catch (error) {
    clearTimeout(timeoutId);
    if (error.name === 'AbortError') {
      console.error('Candidates fetch timeout - API took longer than 30s');
      return { success: false, data: [], error: 'Timeout' };
    }
    console.error('Error fetching candidates:', error);
    return { success: false, data: [], error: error.message };
  }
};

/**
 * Fetch jobs with optional date filtering
 */
export const fetchJobs = async (includeOlderJobs = false) => {
  const { controller, timeoutId } = createAbortController(API_TIMEOUTS.JOBS);
  
  try {
    const response = await fetch(
      `${BACKEND_URL}/api/jobs?limit=${DATA_LIMITS.JOBS}`,
      { signal: controller.signal }
    );
    clearTimeout(timeoutId);
    
    if (response.ok) {
      const data = await response.json();
      let allJobs = Array.isArray(data) ? data : (data.jobs || []);
      
      // Filter jobs by date_added (Juno schema field)
      if (!includeOlderJobs) {
        const sixMonthsAgo = new Date();
        sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6);
        
        allJobs = allJobs.filter(job => {
          if (!job.date_added) return false;
          const jobDate = new Date(job.date_added);
          return jobDate >= sixMonthsAgo;
        });
        
        console.log(`Loaded ${allJobs.length} jobs from last 6 months (filtered from total)`);
      } else {
        console.log(`Loaded ${allJobs.length} jobs (including older jobs)`);
      }
      
      return { success: true, data: allJobs };
    } else {
      console.error('Failed to fetch jobs:', response.status);
      return { success: false, data: [], error: `HTTP ${response.status}` };
    }
  } catch (error) {
    clearTimeout(timeoutId);
    if (error.name === 'AbortError') {
      console.error('Jobs fetch timeout - API took longer than 30s');
      return { success: false, data: [], error: 'Timeout' };
    }
    console.error('Error fetching jobs:', error);
    return { success: false, data: [], error: error.message };
  }
};

/**
 * Fetch clients/customers
 */
export const fetchClients = async () => {
  const { controller, timeoutId } = createAbortController(API_TIMEOUTS.CLIENTS);
  
  try {
    const response = await fetch(`${BACKEND_URL}/api/customers`, {
      signal: controller.signal
    });
    clearTimeout(timeoutId);
    
    if (response.ok) {
      const data = await response.json();
      return { success: true, data: data.customers || [] };
    } else {
      console.error('Failed to fetch clients:', response.status);
      return { success: false, data: [], error: `HTTP ${response.status}` };
    }
  } catch (error) {
    clearTimeout(timeoutId);
    if (error.name === 'AbortError') {
      console.error('Clients fetch timeout');
      return { success: false, data: [], error: 'Timeout' };
    }
    console.error('Error fetching clients:', error);
    return { success: false, data: [], error: error.message };
  }
};

/**
 * Fetch recruiters (users with recruiting role)
 */
export const fetchRecruiters = async () => {
  const { controller, timeoutId } = createAbortController(API_TIMEOUTS.RECRUITERS);
  
  try {
    const response = await fetch(`${BACKEND_URL}/api/users`, {
      signal: controller.signal
    });
    clearTimeout(timeoutId);
    
    if (response.ok) {
      const data = await response.json();
      // Note: Filtering will be done by the hook using isRecruitingRole utility
      return { success: true, data: data.users || [] };
    } else {
      console.error('Failed to fetch recruiters:', response.status);
      return { success: false, data: [], error: `HTTP ${response.status}` };
    }
  } catch (error) {
    clearTimeout(timeoutId);
    if (error.name === 'AbortError') {
      console.error('Recruiters fetch timeout');
      return { success: false, data: [], error: 'Timeout' };
    }
    console.error('Error fetching recruiters:', error);
    return { success: false, data: [], error: error.message };
  }
};
